import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * <ChangePasswordDialog />
 *
 *   Reusable across the Admin dashboard, Owner dashboard, and anywhere else
 *   a logged-in user needs to rotate their password.
 *
 * Props:
 *   open               boolean — controlled visibility
 *   onOpenChange       (boolean) => void
 *   requireCurrent     boolean (default true). Set to false when the account
 *                      doesn't have a password yet (e.g. phone-only owners
 *                      setting one for the first time).
 *   onSuccess          () => void  — fires after a successful change. Use to
 *                                    close the dialog or refresh state.
 */
export default function ChangePasswordDialog({ open, onOpenChange, requireCurrent = true, onSuccess }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm("");
    setShowCurrent(false); setShowNext(false);
    setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange?.(next);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (requireCurrent && !current) return toast.error("Please enter your current password.");
    if (next.length < 6) return toast.error("New password must be at least 6 characters.");
    if (next !== confirm) return toast.error("Passwords do not match.");
    if (requireCurrent && next === current) return toast.error("New password must be different from the current one.");

    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use the new password next time you log in.");
      reset();
      onOpenChange?.(false);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not update password. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <KeyRound size={18} className="text-[#1F4E3D]" />
            Change Password
          </DialogTitle>
          <DialogDescription className="text-xs text-[#4B5563]">
            For your security, you'll need to enter your current password to set a new one.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-1">
          {requireCurrent && (
            <div>
              <Label className="text-xs">Current Password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showCurrent ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                  className="pr-10"
                  data-testid="cp-current-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#111827]"
                  aria-label={showCurrent ? "Hide password" : "Show password"}
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">
              New Password <span className="text-[#4B5563] font-normal">(min 6 characters)</span>
            </Label>
            <div className="relative mt-1.5">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                className="pr-10"
                data-testid="cp-new-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowNext((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#111827]"
                aria-label={showNext ? "Hide password" : "Show password"}
              >
                {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs">Confirm New Password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              className="mt-1.5"
              data-testid="cp-confirm-input"
              required
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={submitting}
              className="border-[#E7DDCF]"
              data-testid="cp-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"
              data-testid="cp-submit-button"
            >
              {submitting
                ? (<><Loader2 size={14} className="animate-spin" /> Updating…</>)
                : ("Update Password")
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
